import { useState, useRef, useEffect } from "react";
import { useHelpFocusBlur } from "../../utils/useHelpFocusBlur";
import ElectricLoad from "../electricLoad";
import ASHRAELoad from "../ASHRAELoad";
import mgaLabels from "../../constants/LABELS";
import { useDualTranslation } from "../../utils/i18nHelpers";
import MGAAPI from "../../api/index";
import "./style.scss";
import { AccordionItem } from "../AccordianItem";
import { addedStepsAtom } from "../../utils/atoms/addedStepsAtom";
import { useAtom } from "jotai";
import { Tabs, useMantineColorScheme } from "@mantine/core";
import { SeIcon } from "@se/web-ui-react";
import PropTypes from "prop-types";
import { helpAtom } from "../../utils/atoms/helpAtom";
import { useAssessmentRouteParams, useCurrentDesignComponentLists } from "../../constants/helper";

const MGA_API = new MGAAPI();
function UploadMethod({
  loadComponents = [],
  projectDetails = {},
  initialTab,
  initialSelectedBuildingType,
  isThermalEnabled,
  openSteps,
  triggerSave,
  form,
  updateLoadState,
  componentDetails,
  handleLoadUpload,
  setLoadingComp
}) {
  const { t } = useDualTranslation();
  const {designId,designVersionId} = useAssessmentRouteParams()
  const [addedStepsSeries] = useAtom(addedStepsAtom);
  const selectedDesignAndDesignVersion = useCurrentDesignComponentLists(projectDetails?.designComponentsList)
  const [getBuildingTypes, setGetBuildingTypes] = useState([]);
  const [ashraeData, setAshraeData] = useState(null);
  const [displayLoader, setDisplayLoader] = useState(false);
  const [displayChart, setDisplayChart] = useState(true);
  const [selectedBuildingType, setSelectedBuildingType] = useState(
    initialSelectedBuildingType
  );
  const { colorScheme } = useMantineColorScheme();

  const [loadDataSet, setLoadDataSet] = useState({
    electrical: [],
    thermal: [],
  });
  const handleLoadData = () => {
    const electricalLoad = selectedDesignAndDesignVersion?.componentDTOList?.find(
      (comp) => comp.componentType === "ElectricalLoad"
    );
    const thermalLoad = selectedDesignAndDesignVersion?.componentDTOList?.find(
      (comp) => comp.componentType === "ThermalLoad"
    );
    setLoadDataSet({
      electrical: electricalLoad || {},
      thermal: thermalLoad || {},
    });
  };

  useEffect(() => {
    handleLoadData();
  }, [projectDetails, designVersionId]);

  useEffect(() => {
    if (initialTab === "ashrae") {
      setLoadDataSet({ electrical: [], thermal: [] });
    }
  }, [initialTab]);

  const [active, setActive] = useState(null);
  const containerRef = useRef(null);
  const [helperAtom, setHelperAtom] = useAtom(helpAtom);
  // Add event delegation for help logic
  useHelpFocusBlur(containerRef, setHelperAtom, helperAtom);

  useEffect(() => {
    if (initialTab === "ashrae") {
      MGA_API.getBuildingTypes()
        .then((res) => {
          const currentScalingFactor =
            componentDetails?.load?.componentAttributes?.loadParameters
              ?.profileDetail?.scalingFactor || 1;

          updateLoadState(
            "componentAttributes.loadParameters.profileDetail",
            "profileDataLoc",
            componentDetails?.load?.componentAttributes?.loadParameters
              ?.profileDetail?.profileDataLoc || "OfficeMedium"
          );
          setGetBuildingTypes(res);
          updateLoadState(
            "componentAttributes.loadParameters.profileDetail",
            "scalingFactor",
            currentScalingFactor
          );
          updateLoadState(
            "componentAttributes.loadParameters",
            "scalingFactorElec",
            componentDetails?.load?.componentAttributes?.loadParameters
              ?.scalingFactorElec ?? currentScalingFactor
          );
          updateLoadState(
            "componentAttributes.loadParameters",
            "scalingFactorTherm",
            componentDetails?.load?.componentAttributes?.loadParameters
              ?.scalingFactorTherm ?? currentScalingFactor
          );
          updateLoadState(
            "componentAttributes.loadParameters.profileDetail",
            "useThermalLoad",
            componentDetails?.load?.componentAttributes?.loadParameters
              ?.profileDetail?.useThermalLoad ?? true
          );
        })
        .catch((error) =>
          console.error("Error fetching building types:", error)
        );
    }
  }, [initialTab, designVersionId]);

  const header = () => (
    <span data-id="accordion-load-2">
      <span className="stepText col-md-1 px-0">
        {`Step ${addedStepsSeries.indexOf("load") + 1}`}: B.{" "}
      </span>
      <span
        className="ml-2 mt-0 col-md-8 px-0"
        style={{ color: colorScheme === "dark" ? "#d5d7e0" : "#475f7b" }}
      >
        {t("uploadMethod")}
      </span>
    </span>
  );

  // Only run help logic if the event target is the container itself
  const handleContainerClick = (event) => {
    const interactiveTags = ["INPUT", "SELECT", "TEXTAREA", "BUTTON", "LABEL"];
    if (interactiveTags.includes(event.target.tagName)) return;
    const cardElement = document.querySelector('[card-id="accordion-load-2"]');
    if (cardElement) {
      cardElement.scrollIntoView({ behavior: "smooth", block: "center" });
      cardElement.style.transition = "background-color 0.5s ease";
      cardElement.style.border = "2px solid #42b4e6";
      setTimeout(() => {
        cardElement.style.border = "";
      }, 2000);
    }
  };

  const handleContainerKeyDown = (event) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      handleContainerClick(event);
    }
  };
  const children = () => (
    <div
      className="row"
      ref={containerRef}
      data-id="accordion-load-2"
      onClick={handleContainerClick}
      onKeyDown={handleContainerKeyDown}
      tabIndex={0}
      role="button"
    >
      {initialTab !== "ashrae" && (
        <ElectricLoad
          componentData={loadDataSet?.electrical || {}} // data which we are sending to chart
          componentDataThermal={loadDataSet?.thermal || {}}
          displayLoader={displayLoader}
          displayChart={displayChart}
          triggerSave={triggerSave}
          projectDetails={projectDetails}
          form={form}
          updateLoadState={updateLoadState}
          componentDetails={componentDetails}
          setLoadDataSet={setLoadDataSet}
          handleLoadUpload={handleLoadUpload}
          setLoadingComp={setLoadingComp}
        />
      )}
      <div className="col-sm-12">
        {initialTab === "ashrae" && (
          <ASHRAELoad
            componentData={loadComponents.find(
              (comp) => comp.dataSource === "Ashrae"
            )}
            getBuildingTypes={getBuildingTypes}
            selectedBuildingType={selectedBuildingType}
            setSelectedBuildingType={setSelectedBuildingType}
            displayLoader={displayLoader}
            displayChart={displayChart}
            ashraeData={ashraeData}
            projectDetails={projectDetails}
            setDisplayLoader={setDisplayLoader}
            form={form}
            updateLoadState={updateLoadState}
            loadParameters={componentDetails?.load}
          />
        )}
      </div>
    </div>
  );
  const handleToggle = (index) => {
    if (active === index) {
      setActive(null);
    } else {
      setActive(index);
    }
  };
  return (
    <AccordionItem
      id={2}
      handleToggle={handleToggle}
      header={header()}
      key={2}
      active={active}
      children={children()}
      openSteps={openSteps}
      height="fit-content"
    ></AccordionItem>
  );
}
UploadMethod.propTypes = {
  projectDetails: PropTypes.object,
  componentDetails: PropTypes.object,
  loadComponents: PropTypes.array,
  initialTab: PropTypes.string,
  initialSelectedBuildingType: PropTypes.string,
  isThermalEnabled: PropTypes.bool,
  openSteps: PropTypes.bool,
  triggerSave: PropTypes.bool,
  form: PropTypes.object,
  updateLoadState: PropTypes.func,
};

export default UploadMethod;
